import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createDealsFromCrmContacts, createDealsFromCompanies, syncEmailActivitiesForDeal } from '../../api/crmApi';
import { analyzeCrmImport, analyzePipedriveImport, startCrmImport, startPipedriveImport } from '../../api/crmImportApi';
import { activitiesKeys } from '../activities/keys';
import { dealsKeys } from '../deals/keys';

/**
 * Analyze parsed CSV headers → mapping suggestions + available target fields.
 */
export function useAnalyzeCrmImport() {
	return useMutation({
		mutationKey: ['crm', 'import', 'analyze'],
		mutationFn: analyzeCrmImport,
		meta: { suppressGlobalError: true },
	});
}

/**
 * Start a CRM import (contact + company single-file).
 * Cache invalidation happens once the import completes (inline or via polling),
 * see useImportJobPolling / the wizard's completion handler.
 */
export function useStartCrmImport() {
	return useMutation({
		mutationKey: ['crm', 'import', 'start'],
		mutationFn: startCrmImport,
		meta: { suppressGlobalError: true },
	});
}

/**
 * Analyze (classify + stage) uploaded Pipedrive export files.
 */
export function useAnalyzePipedriveImport() {
	return useMutation({
		mutationKey: ['crm', 'import', 'pipedrive', 'analyze'],
		mutationFn: analyzePipedriveImport,
		meta: { suppressGlobalError: true },
	});
}

/**
 * Start the Pipedrive migration for an analyzed batch.
 */
export function useStartPipedriveImport() {
	return useMutation({
		mutationKey: ['crm', 'import', 'pipedrive', 'start'],
		mutationFn: startPipedriveImport,
		meta: { suppressGlobalError: true },
	});
}

// Transfer companies (leads or CRM companies) → CRM deals

export function useCreateDealsFromCompanies() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: ['crm', 'deals', 'createFromCompanies'],
		mutationFn: createDealsFromCompanies,
		onSuccess: () => qc.invalidateQueries({ queryKey: dealsKeys.all }),
	});
}

export function useCreateDealsFromCrmContacts() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: ['crm', 'deals', 'createFromCrmContacts'],
		mutationFn: createDealsFromCrmContacts,
		onSuccess: () => qc.invalidateQueries({ queryKey: dealsKeys.all }),
	});
}

export function useSyncEmailActivitiesForDeal() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: ['crm', 'deals', 'syncEmailActivities'],
		mutationFn: ({ dealId, sinceIso = null }) => syncEmailActivitiesForDeal(dealId, { sinceIso }),
		// Synced emails appear as activities — refresh deal activities + detail
		onSuccess: (_data, variables) => {
			if (variables?.dealId) {
				qc.invalidateQueries({ queryKey: activitiesKeys.byDeal(variables.dealId) });
				qc.invalidateQueries({ queryKey: dealsKeys.detail(variables.dealId) });
			}
		},
	});
}
